import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Navbar from "../components/Navbar";
import { api, API, CATEGORIES } from "../lib/api";
import { useAuth, formatErr } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import RichTextEditor from "../components/RichTextEditor";
import { toast } from "sonner";
import { Loader2, ArrowLeft, Plus, Sparkles, Trash2, FileText, Send, Save, ExternalLink, Copy, RefreshCw } from "lucide-react";
import { AttachButton, PendingAttachments, MessageAttachments } from "../components/ChatAttachments";

const fmtEur = (n) => `€ ${Number(n || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const UNITS = ["pz", "h", "m", "m2", "kW", "kg", "gg"];
const VAT_OPTIONS = [0, 4, 10, 22];

function computeTotals(items, defaultVat, discount) {
  const def = Number(defaultVat) || 0;
  const baseByRate = {};
  let subtotal = 0;
  (items || []).forEach(it => {
    const qty = Number(it.quantity) || 0;
    const up = Number(it.unit_price) || 0;
    const line = qty * up;
    subtotal += line;
    const raw = it.vat_rate;
    const r = raw === null || raw === undefined || raw === "" ? def : Number(raw);
    baseByRate[r] = (baseByRate[r] || 0) + line;
  });
  const disc = Number(discount) || 0;
  const net = Math.max(0, subtotal - disc);
  const scale = subtotal > 0 ? net / subtotal : 0;
  const breakdown = [];
  let vatTotal = 0;
  Object.keys(baseByRate).map(Number).sort((a, b) => a - b).forEach(rate => {
    const base = Math.round(baseByRate[rate] * scale * 100) / 100;
    const amount = Math.round(base * rate) / 100;
    vatTotal += amount;
    breakdown.push({ rate, base, amount });
  });
  vatTotal = Math.round(vatTotal * 100) / 100;
  return {
    subtotal: Math.round(subtotal * 100) / 100,
    discount: disc,
    net: Math.round(net * 100) / 100,
    vat_amount: vatTotal,
    total: Math.round((net + vatTotal) * 100) / 100,
    vat_breakdown: breakdown,
  };
}

export default function AdminQuoteDraftDetail() {
  const { id } = useParams();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [saving, setSaving] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const isStaff = user?.role === "personale" || user?.role === "admin";

  useEffect(() => { if (!loading && !isStaff) navigate("/login"); }, [user, loading, navigate, isStaff]);

  const load = async () => {
    try { const r = await api.get(`/admin/quote-drafts/${id}`); setD(r.data); }
    catch { toast.error("Non trovato"); navigate("/admin/preventivi-staff"); }
  };
  useEffect(() => { if (isStaff) load(); /* eslint-disable-next-line */ }, [id, user]);

  const patch = (u) => setD(prev => ({ ...prev, ...u, totals: computeTotals(u.items ?? prev.items, u.vat_rate ?? prev.vat_rate, u.discount ?? prev.discount) }));
  const setItem = (i, k, v) => {
    const items = [...(d.items || [])];
    if (k === "description" || k === "unit") items[i] = { ...items[i], [k]: v };
    else if (k === "vat_rate") items[i] = { ...items[i], vat_rate: v === "" || v === "default" ? null : Number(v) };
    else items[i] = { ...items[i], [k]: Number(v) || 0 };
    patch({ items });
  };
  const addItem = () => patch({ items: [...(d.items || []), { description: "", quantity: 1, unit: "pz", unit_price: 0, vat_rate: null }] });
  const rmItem = (i) => patch({ items: (d.items || []).filter((_, idx) => idx !== i) });

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.patch(`/admin/quote-drafts/${id}`, {
        client_name: d.client_name, client_email: d.client_email, client_phone: d.client_phone,
        category: d.category, city: d.city, address: d.address, description: d.description,
        items: d.items, vat_rate: d.vat_rate, discount: d.discount, notes: d.notes,
        validity_days: d.validity_days,
      });
      setD(r.data);
      toast.success("Salvato");
    } catch (e) { toast.error(formatErr(e)); } finally { setSaving(false); }
  };

  const pdfUrl = `${API}/admin/quote-drafts/${id}/pdf`;
  const totals = useMemo(() => computeTotals(d?.items || [], d?.vat_rate || 22, d?.discount || 0), [d?.items, d?.vat_rate, d?.discount]);

  if (!d) return <div className="min-h-screen"><Navbar/><div className="p-12 text-center text-slate-500">Caricamento...</div></div>;

  const isSent = d.status === "inviato";

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar/>
      <div className="max-w-6xl mx-auto p-6">
        <Link to="/admin/preventivi-staff" className="inline-flex items-center gap-2 text-slate-600 hover:text-blue-800 mb-4 text-sm" data-testid="draft-back-link">
          <ArrowLeft className="w-4 h-4"/> Tutti i preventivi staff
        </Link>

        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div>
            <div className="text-xs font-mono text-slate-500 flex items-center gap-2">
              {d.draft_id}
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${isSent ? "bg-emerald-100 text-emerald-900" : "bg-slate-100 text-slate-800"}`}>{d.status}</span>
              {d.quote_id && <Link to={`/admin/preventivi/${d.quote_id}`} className="text-blue-800 hover:underline">Richiesta collegata: {d.quote_id}</Link>}
            </div>
            <h1 className="heading text-3xl font-extrabold text-slate-900">{d.client_name}</h1>
            <div className="text-slate-600 text-sm mt-1">{d.client_email || "—"} · {d.client_phone || "—"}</div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button onClick={() => setAiOpen(true)} className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="draft-ai-open">
              <Sparkles className="w-4 h-4 mr-2"/> Chat IA preventivo
            </Button>
            <a href={pdfUrl} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" className="border-blue-200 text-blue-900" data-testid="draft-pdf-btn">
                <FileText className="w-4 h-4 mr-2"/> Anteprima PDF
              </Button>
            </a>
            {d.client_email && (
              <Button onClick={() => setSendOpen(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="draft-send-btn">
                <Send className="w-4 h-4 mr-2"/>{isSent ? "Reinvia al cliente" : "Invia al cliente"}
              </Button>
            )}
            <Button onClick={save} disabled={saving} className="bg-blue-800 hover:bg-blue-900" data-testid="draft-save-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2"/> : <Save className="w-4 h-4 mr-2"/>}Salva
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="p-5 lg:col-span-2 space-y-4">
            <h3 className="heading font-bold text-slate-900">Dati cliente & lavoro</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Nome cliente"><Input value={d.client_name || ""} onChange={e=>patch({client_name: e.target.value})} data-testid="d-client-name"/></Field>
              <Field label="Email"><Input value={d.client_email || ""} onChange={e=>patch({client_email: e.target.value})} data-testid="d-client-email"/></Field>
              <Field label="Telefono"><Input value={d.client_phone || ""} onChange={e=>patch({client_phone: e.target.value})} data-testid="d-client-phone"/></Field>
              <Field label="Città"><Input value={d.city || ""} onChange={e=>patch({city: e.target.value})} data-testid="d-city"/></Field>
              <Field label="Indirizzo"><Input value={d.address || ""} onChange={e=>patch({address: e.target.value})} data-testid="d-address"/></Field>
              <Field label="Categoria">
                <Select value={d.category || "none"} onValueChange={v => patch({category: v === "none" ? "" : v})}>
                  <SelectTrigger data-testid="d-category"><SelectValue placeholder="—"/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">—</SelectItem>
                    {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <Field label="Descrizione lavoro (usata dall'IA)">
              <textarea className="w-full min-h-[80px] rounded-md border border-slate-300 p-2 text-sm" value={d.description || ""} onChange={e=>patch({description: e.target.value})} data-testid="d-description"/>
            </Field>

            <div className="pt-2">
              <div className="flex items-center justify-between mb-2">
                <h3 className="heading font-bold text-slate-900">Voci del preventivo</h3>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => setAiOpen(true)} className="border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-900" data-testid="d-ai-suggest-btn">
                    <Sparkles className="w-4 h-4 mr-1"/> Chat IA
                  </Button>
                  <Button size="sm" onClick={addItem} className="bg-blue-800" data-testid="d-add-item"><Plus className="w-4 h-4 mr-1"/>Aggiungi</Button>
                </div>
              </div>
              <div className="border rounded-md overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-600 text-xs uppercase">
                    <tr>
                      <th className="text-left px-2 py-2 w-8">#</th>
                      <th className="text-left px-2 py-2">Descrizione</th>
                      <th className="text-right px-2 py-2 w-16">Q.tà</th>
                      <th className="text-left px-2 py-2 w-20">U.M.</th>
                      <th className="text-right px-2 py-2 w-24">Prezzo</th>
                      <th className="text-left px-2 py-2 w-20">IVA %</th>
                      <th className="text-right px-2 py-2 w-24">Importo</th>
                      <th className="w-10"></th>
                    </tr>
                  </thead>
                  <tbody data-testid="d-items">
                    {(d.items || []).length === 0 && (
                      <tr><td colSpan={8} className="text-center py-6 text-slate-400 text-sm">Nessuna voce. Aggiungi manualmente o usa l'IA.</td></tr>
                    )}
                    {(d.items || []).map((it, i) => (
                      <tr key={i} className="border-t" data-testid={`d-item-${i}`}>
                        <td className="px-2 py-1 text-slate-500 text-xs">{i+1}</td>
                        <td className="px-2 py-1">
                          <Input value={it.description} onChange={e=>setItem(i, "description", e.target.value)} className="h-8" data-testid={`d-item-desc-${i}`}/>
                        </td>
                        <td className="px-2 py-1">
                          <Input type="number" step="0.5" value={it.quantity} onChange={e=>setItem(i, "quantity", e.target.value)} className="h-8 text-right" data-testid={`d-item-qty-${i}`}/>
                        </td>
                        <td className="px-2 py-1">
                          <Select value={it.unit || "pz"} onValueChange={v=>setItem(i, "unit", v)}>
                            <SelectTrigger className="h-8"><SelectValue/></SelectTrigger>
                            <SelectContent>{UNITS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                          </Select>
                        </td>
                        <td className="px-2 py-1">
                          <Input type="number" step="0.01" value={it.unit_price} onChange={e=>setItem(i, "unit_price", e.target.value)} className="h-8 text-right" data-testid={`d-item-price-${i}`}/>
                        </td>
                        <td className="px-2 py-1">
                          <Select value={it.vat_rate == null ? "default" : String(it.vat_rate)}
                                  onValueChange={v => setItem(i, "vat_rate", v)}>
                            <SelectTrigger className="h-8" data-testid={`d-item-vat-${i}`}><SelectValue/></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="default">— ({(d.vat_rate ?? 22)}%)</SelectItem>
                              {VAT_OPTIONS.map(v => <SelectItem key={v} value={String(v)}>{v}%</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-2 py-1 text-right text-slate-700 font-medium">{fmtEur((Number(it.quantity)||0) * (Number(it.unit_price)||0))}</td>
                        <td className="px-1 py-1">
                          <button onClick={() => rmItem(i)} className="text-slate-400 hover:text-red-600 p-1" title="Rimuovi" data-testid={`d-item-rm-${i}`}>
                            <Trash2 className="w-4 h-4"/>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <Field label="Note (visibili nel PDF)">
              <textarea className="w-full min-h-[80px] rounded-md border border-slate-300 p-2 text-sm" value={d.notes || ""} onChange={e=>patch({notes: e.target.value})} data-testid="d-notes"/>
            </Field>
          </Card>

          <Card className="p-5 space-y-4">
            <h3 className="heading font-bold text-slate-900">Totali</h3>
            <div className="grid grid-cols-3 gap-2">
              <Field label="IVA % default"><Input type="number" step="0.5" value={d.vat_rate ?? 22} onChange={e=>patch({vat_rate: Number(e.target.value) || 0})} data-testid="d-vat"/></Field>
              <Field label="Sconto €"><Input type="number" step="0.01" value={d.discount ?? 0} onChange={e=>patch({discount: Number(e.target.value) || 0})} data-testid="d-discount"/></Field>
              <Field label="Validità gg"><Input type="number" value={d.validity_days ?? 30} onChange={e=>patch({validity_days: Number(e.target.value) || 0})} data-testid="d-validity"/></Field>
            </div>
            <p className="text-[11px] text-slate-500 -mt-2">L'IVA di default si applica solo alle voci senza aliquota specifica.</p>
            <div className="bg-slate-50 rounded-md p-3 space-y-1 text-sm">
              <Row k="Imponibile" v={fmtEur(totals.subtotal)}/>
              {totals.discount > 0 && <Row k="Sconto" v={`- ${fmtEur(totals.discount)}`}/>}
              {totals.discount > 0 && <Row k="Netto" v={fmtEur(totals.net)}/>}
              {(totals.vat_breakdown || []).map(b => (
                <Row key={b.rate} k={<span>IVA {b.rate}% <span className="text-slate-400 text-xs">su {fmtEur(b.base)}</span></span>} v={fmtEur(b.amount)}/>
              ))}
              <div className="border-t border-slate-300 pt-1 mt-1">
                <Row k={<b>Totale</b>} v={<b className="text-lg text-blue-900" data-testid="d-total">{fmtEur(totals.total)}</b>}/>
              </div>
            </div>
            {d.status === "inviato" && d.pdf_file_id && (
              <div className="border rounded-md p-3 bg-emerald-50 text-emerald-900 text-xs">
                <div className="font-semibold mb-1">Inviato al cliente</div>
                <div>Il PDF finale è archiviato ed è disponibile:</div>
                <a href={`${API}/files/${d.pdf_file_id}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 mt-1 text-emerald-900 underline">
                  <ExternalLink className="w-3 h-3"/> apri PDF inviato
                </a>
              </div>
            )}
          </Card>
        </div>
      </div>

      <AiSuggestDialog open={aiOpen} onOpenChange={setAiOpen} draft={d}
                       onApply={(items, notes) => { patch({ items: [...(d.items || []), ...items], notes: (d.notes ? d.notes + "\n" : "") + (notes || "") }); setAiOpen(false); toast.success(`${items.length} voci aggiunte`); }}/>
      <SendDialog open={sendOpen} onOpenChange={setSendOpen} draft={d} onSent={()=>{ setSendOpen(false); load(); }}/>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs font-semibold uppercase tracking-widest text-slate-600 mb-1 block">{label}</label>
      {children}
    </div>
  );
}
function Row({ k, v }) {
  return <div className="flex items-center justify-between"><span className="text-slate-600">{k}</span><span>{v}</span></div>;
}

function AiSuggestDialog({ open, onOpenChange, draft, onApply }) {
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  const loadHistory = async () => {
    if (!draft?.draft_id) return;
    setLoading(true);
    try {
      const r = await api.get(`/admin/quote-drafts/${draft.draft_id}/ai-chat`);
      setMsgs(r.data || []);
    } catch { /* ignore */ } finally { setLoading(false); }
  };

  useEffect(() => {
    if (open) { setText(""); setPending([]); loadHistory(); }
    // eslint-disable-next-line
  }, [open, draft?.draft_id]);

  useEffect(() => { scrollRef.current?.scrollTo({ top: 99999, behavior: "smooth" }); }, [msgs, busy]);

  const send = async () => {
    if ((!text.trim() && pending.length === 0) || busy) return;
    const t = text.trim() || "Analizza gli allegati";
    const attachments = pending.map(p => p.file_id);
    const opt = [...pending];
    setText(""); setPending([]); setBusy(true);
    setMsgs(m => [...m, { role: "user", content: t, attachments: opt }]);
    try {
      const r = await api.post(`/admin/quote-drafts/${draft.draft_id}/ai-chat`,
        { message: t, attachments });
      setMsgs(m => [...m, { role: "assistant", content: r.data.reply, proposal: r.data.proposal }]);
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };

  const reset = async () => {
    if (!window.confirm("Ricominciare la conversazione con l'IA? Il preventivo NON verrà cancellato, solo la chat con l'IA.")) return;
    try {
      await api.delete(`/admin/quote-drafts/${draft.draft_id}/ai-chat`);
      setMsgs([]);
      toast.success("Chat IA reimpostata");
    } catch (e) { toast.error(formatErr(e)); }
  };

  const applyProposal = async (proposal) => {
    if (!proposal || !proposal.items?.length) return;
    // Auto-save unlinked items into the catalog (flag needs_review:true) so the
    // prezziario grows organically. Items that already came from the catalog
    // (catalog_id set) are skipped.
    const unsaved = (proposal.items || []).filter(it =>
      !it.catalog_id && (it.description || "").trim().length >= 2
    );
    if (unsaved.length) {
      const results = await Promise.allSettled(unsaved.map(it =>
        api.post("/material-catalog", {
          description: it.description,
          brand: it.brand || null,
          model: it.model || null,
          article_code: it.article_code || null,
          unit: it.unit || "pz",
          sell_price: Number(it.unit_price) || 0,
          purchase_price: null,
          category: it.category || null,
          needs_review: true,
        })
      ));
      const ok = results.filter(r => r.status === "fulfilled").length;
      if (ok > 0) toast.success(`${ok} ${ok === 1 ? "articolo salvato" : "articoli salvati"} nel catalogo (da revisionare)`);
    }
    onApply(proposal.items, proposal.notes || "");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl h-[85vh] flex flex-col p-0">
        <DialogHeader className="p-4 bg-gradient-to-r from-amber-500 to-orange-500 rounded-t-lg">
          <DialogTitle className="text-white heading flex items-center gap-2">
            <Sparkles className="w-5 h-5"/>Assistente IA preventivo
            <button onClick={reset} className="ml-auto text-white/80 hover:text-white text-xs flex items-center gap-1" title="Ricomincia la conversazione IA" data-testid="ai-reset-btn">
              <RefreshCw className="w-3.5 h-3.5"/> Ricomincia
            </button>
          </DialogTitle>
          <p className="text-amber-50 text-xs">Chatta con l'IA: risponderà con domande finché non avrà tutte le info per proporti il preventivo. Puoi allegare foto/PDF del cantiere.</p>
        </DialogHeader>
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50" data-testid="ai-chat-messages">
          {loading && <div className="text-center"><Loader2 className="w-5 h-5 animate-spin text-amber-500 mx-auto"/></div>}
          {!loading && msgs.length === 0 && (
            <div className="text-center text-sm text-slate-500 mt-8 space-y-2">
              <p>Inizia raccontando il lavoro all'IA.</p>
              <p className="text-xs">Es: "Cliente vuole un condizionatore in camera da 15mq, quadro elettrico già ok"</p>
            </div>
          )}
          {msgs.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] rounded-lg text-sm ${m.role === "user" ? "bg-amber-500 text-white px-3 py-2" : "bg-white border border-slate-200 text-slate-800 px-3 py-2 shadow-sm"}`}>
                <div className="whitespace-pre-wrap">{m.content}</div>
                <MessageAttachments attachments={m.attachments} side={m.role === "user" ? "right" : "left"}/>
                {m.proposal && m.proposal.items?.length > 0 && (
                  <ProposalCard proposal={m.proposal} onApply={(updatedItems) => applyProposal({ ...m.proposal, items: updatedItems })}/>
                )}
              </div>
            </div>
          ))}
          {busy && (
            <div className="flex justify-start">
              <div className="bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-500 flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin text-amber-500"/> L'IA sta pensando...
              </div>
            </div>
          )}
        </div>
        <PendingAttachments items={pending} onRemove={(id) => setPending(p => p.filter(x => x.file_id !== id))}/>
        <div className="p-3 border-t flex gap-2 items-center bg-white">
          <AttachButton onUploaded={(a) => setPending(p => [...p, ...a])} testId="ai-chat-attach"/>
          <Input value={text} onChange={e=>setText(e.target.value)} onKeyDown={e=>e.key==="Enter" && send()}
                 placeholder="Rispondi all'IA o chiedi il preventivo..." data-testid="ai-chat-input"
                 disabled={busy}/>
          <Button onClick={send} disabled={busy || (!text.trim() && pending.length === 0)}
                  data-testid="ai-chat-send" className="bg-amber-500 hover:bg-amber-600">
            {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <Send className="w-4 h-4"/>}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ProposalCard({ proposal, onApply }) {
  const [items, setItems] = useState(proposal.items || []);
  const [newItemIdx, setNewItemIdx] = useState(null);
  useEffect(() => { setItems(proposal.items || []); }, [proposal]);

  const subtotal = items.reduce((s, it) => s + (Number(it.quantity) || 0) * (Number(it.unit_price) || 0), 0);
  const anyVat = items.some(it => it.vat_rate !== undefined && it.vat_rate !== null);

  const pickCandidate = async (idx, cand) => {
    const next = [...items];
    next[idx] = {
      ...next[idx],
      description: cand.description,
      brand: cand.brand || next[idx].brand,
      model: cand.model || next[idx].model,
      article_code: cand.article_code || next[idx].article_code,
      unit: cand.unit || next[idx].unit,
      unit_price: Number(cand.sell_price ?? cand.default_price ?? next[idx].unit_price),
      catalog_id: cand.catalog_id,
      catalog_match: { status: "linked", item: cand },
    };
    setItems(next);
    try { await api.post(`/material-catalog/${cand.catalog_id}/bump-usage`); } catch {}
    toast.success(`"${cand.description}" preso dal catalogo`);
  };

  const openNewForm = (idx) => setNewItemIdx(idx);
  const closeNewForm = () => setNewItemIdx(null);

  const saveNewItem = async (idx, formData) => {
    try {
      const r = await api.post("/material-catalog", formData);
      const cand = r.data;
      await pickCandidate(idx, cand);
      toast.success("Articolo salvato nel catalogo");
      closeNewForm();
    } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="mt-3 -mx-3 -mb-2 rounded-b-lg border-t border-amber-200 bg-amber-50 px-3 py-2" data-testid="ai-proposal">
      <div className="text-xs font-semibold uppercase tracking-widest text-amber-900 mb-1 flex items-center gap-2">
        <Sparkles className="w-3.5 h-3.5"/> Proposta di preventivo
      </div>
      <div className="space-y-2">
        {items.map((it, i) => {
          const linked = it.catalog_match?.status === "linked";
          const candidates = it.catalog_match?.status === "candidates" ? (it.catalog_match.candidates || []) : [];
          const needsForm = !linked && candidates.length === 0;
          return (
            <div key={i} className="rounded-md border border-amber-200 bg-white px-2 py-1.5 text-xs">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="text-slate-900 font-medium truncate">
                    {it.brand && <span className="text-blue-800">{it.brand} </span>}
                    {it.model && <span className="text-slate-700">{it.model} · </span>}
                    {it.description}
                    {it.article_code && <span className="ml-1 text-[10px] font-mono text-slate-400">[{it.article_code}]</span>}
                  </div>
                  <div className="text-[11px] text-slate-500">
                    {it.quantity} {it.unit} × {fmtEur(it.unit_price)}{it.vat_rate != null && ` · IVA ${it.vat_rate}%`}
                    {" = "}<span className="font-semibold">{fmtEur((Number(it.quantity)||0)*(Number(it.unit_price)||0))}</span>
                  </div>
                </div>
                {linked && (
                  <span className="text-[10px] bg-emerald-100 text-emerald-900 px-1.5 py-0.5 rounded font-semibold whitespace-nowrap">✓ dal catalogo</span>
                )}
              </div>
              {!linked && candidates.length > 0 && (
                <div className="mt-1.5 border-t border-amber-100 pt-1.5">
                  <div className="text-[10px] uppercase tracking-widest text-amber-900 mb-1">Trovati nel catalogo:</div>
                  <div className="space-y-1">
                    {candidates.map(c => {
                      const d = c.price_checked_at || c.updated_at || c.created_at;
                      const stale = d && (Date.now() - new Date(d).getTime()) > 180 * 86400 * 1000;
                      return (
                        <button key={c.catalog_id} onClick={() => pickCandidate(i, c)}
                                className="w-full text-left bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded px-2 py-1 flex items-center justify-between gap-2"
                                data-testid={`cand-${c.catalog_id}`}>
                          <div className="truncate text-slate-800">
                            <span className="font-medium">{c.brand || "—"}</span>{" "}
                            {c.model && <span className="text-slate-600">{c.model} · </span>}
                            <span>{c.description}</span>
                            {c.article_code && <span className="text-slate-400 font-mono text-[10px]"> [{c.article_code}]</span>}
                            {stale && <span title="Prezzo oltre 6 mesi, controlla!" className="ml-1 text-yellow-700 font-bold">⏰</span>}
                            {c.needs_review && <span title="Da revisionare" className="ml-1 text-amber-700 font-bold">⚠</span>}
                          </div>
                          <span className="whitespace-nowrap text-emerald-900 font-semibold">{fmtEur(c.sell_price ?? c.default_price)}</span>
                        </button>
                      );
                    })}
                    <button onClick={() => openNewForm(i)}
                            className="w-full text-center bg-white hover:bg-slate-50 border border-dashed border-slate-300 rounded px-2 py-1 text-slate-600 hover:text-slate-900"
                            data-testid={`new-${i}`}>
                      + Nessuno di questi: schedane uno nuovo
                    </button>
                  </div>
                </div>
              )}
              {needsForm && (
                <div className="mt-1.5 border-t border-amber-100 pt-1.5">
                  <button onClick={() => openNewForm(i)}
                          className="w-full text-center bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded px-2 py-1 text-blue-900 font-semibold"
                          data-testid={`new-item-${i}`}>
                    + Articolo nuovo: compila la scheda per salvarlo in catalogo
                  </button>
                </div>
              )}
              {newItemIdx === i && (
                <NewCatalogItemInlineForm initial={it} onCancel={closeNewForm} onSave={(d) => saveNewItem(i, d)}/>
              )}
            </div>
          );
        })}
      </div>
      <div className="mt-2 flex items-center justify-between">
        <div className="text-xs text-amber-900"><span className="font-semibold">Subtotale IVA escl.</span></div>
        <div className="text-sm font-bold text-amber-900">{fmtEur(subtotal)}</div>
      </div>
      {proposal.notes && (<div className="text-[11px] text-amber-900 mt-2 italic">Note IA: {proposal.notes}</div>)}
      <Button onClick={() => onApply(items)} size="sm" className="mt-2 w-full bg-blue-800 hover:bg-blue-900" data-testid="ai-proposal-apply">
        <Copy className="w-3.5 h-3.5 mr-1"/> Aggiungi queste voci al preventivo
      </Button>
    </div>
  );
}

function NewCatalogItemInlineForm({ initial, onCancel, onSave }) {
  const [f, setF] = useState({
    description: initial?.description || "",
    brand: initial?.brand || "",
    model: initial?.model || "",
    article_code: initial?.article_code || "",
    unit: initial?.unit || "pz",
    purchase_price: "",
    sell_price: initial?.unit_price != null ? String(initial.unit_price) : "",
    category: "",
  });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!f.description.trim()) { toast.error("Descrizione obbligatoria"); return; }
    const sp = parseFloat(f.sell_price || "0");
    if (Number.isNaN(sp) || sp < 0) { toast.error("Prezzo vendita non valido"); return; }
    const pp = f.purchase_price === "" ? null : parseFloat(f.purchase_price);
    setBusy(true);
    try {
      await onSave({
        description: f.description.trim(),
        brand: f.brand.trim() || null,
        model: f.model.trim() || null,
        article_code: f.article_code.trim() || null,
        unit: f.unit,
        sell_price: sp,
        purchase_price: pp,
        category: f.category.trim() || null,
      });
    } finally { setBusy(false); }
  };
  const S = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <div className="mt-2 p-2 rounded-md border border-blue-200 bg-blue-50 space-y-1.5" data-testid="new-item-form">
      <div className="text-[10px] uppercase tracking-widest text-blue-900 font-semibold">Nuovo articolo da salvare in catalogo</div>
      <div className="grid grid-cols-3 gap-1.5">
        <input className="col-span-1 border rounded px-2 py-1 text-xs" placeholder="Marchio" value={f.brand} onChange={S("brand")} data-testid="nf-brand"/>
        <input className="col-span-1 border rounded px-2 py-1 text-xs" placeholder="Modello" value={f.model} onChange={S("model")} data-testid="nf-model"/>
        <input className="col-span-1 border rounded px-2 py-1 text-xs" placeholder="Codice articolo" value={f.article_code} onChange={S("article_code")} data-testid="nf-code"/>
        <input className="col-span-3 border rounded px-2 py-1 text-xs" placeholder="Descrizione *" value={f.description} onChange={S("description")} data-testid="nf-desc"/>
        <select className="col-span-1 border rounded px-2 py-1 text-xs" value={f.unit} onChange={e => setF({ ...f, unit: e.target.value })} data-testid="nf-unit">
          {["pz","m","m2","kg","l","conf","h","kW"].map(u => <option key={u} value={u}>{u}</option>)}
        </select>
        <input className="col-span-1 border rounded px-2 py-1 text-xs" placeholder="€ acquisto (costo)" type="number" step="0.01" value={f.purchase_price} onChange={S("purchase_price")} data-testid="nf-pprice"/>
        <input className="col-span-1 border rounded px-2 py-1 text-xs" placeholder="€ vendita *" type="number" step="0.01" value={f.sell_price} onChange={S("sell_price")} data-testid="nf-sprice"/>
        <input className="col-span-3 border rounded px-2 py-1 text-xs" placeholder="Categoria (opzionale)" value={f.category} onChange={S("category")} data-testid="nf-cat"/>
      </div>
      <div className="flex gap-1.5 justify-end">
        <button onClick={onCancel} disabled={busy} className="text-xs px-2 py-1 rounded border border-slate-300 bg-white hover:bg-slate-50" data-testid="nf-cancel">Annulla</button>
        <button onClick={submit} disabled={busy} className="text-xs px-2 py-1 rounded bg-blue-800 hover:bg-blue-900 text-white font-semibold disabled:opacity-40" data-testid="nf-save">
          {busy ? "Salvo…" : "Salva in catalogo e usa"}
        </button>
      </div>
    </div>
  );
}

function SendDialog({ open, onOpenChange, draft, onSent }) {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSubject(`Preventivo Energy World - ${draft?.draft_id}`);
    setBody(
      `<p>Buongiorno ${draft?.client_name || ""},</p>` +
      `<p>in allegato le inviamo il preventivo richiesto${draft?.category ? " per " + draft.category : ""}.</p>` +
      `<p>Per qualsiasi chiarimento restiamo a disposizione.</p>` +
      `<p>Cordiali saluti,<br>Team Energy World<br>Orzinuovi (BS)</p>`
    );
  }, [open, draft]);

  const send = async () => {
    if (!subject.trim() || !body.trim()) { toast.error("Compila oggetto e corpo"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/admin/quote-drafts/${draft.draft_id}/send-email`, { subject, body });
      if (r.data?.ok) {
        toast.success("Email inviata al cliente con PDF!");
        onSent();
      } else {
        toast.warning(r.data?.warning || "Email non inviata");
      }
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="heading flex items-center gap-2"><Send className="w-5 h-5 text-blue-800"/>Invia preventivo al cliente</DialogTitle>
          <p className="text-xs text-slate-500">Destinatario: <span className="font-mono">{draft?.client_email}</span> · Il PDF verrà generato e allegato via link</p>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <Field label="Oggetto"><Input value={subject} onChange={e=>setSubject(e.target.value)} data-testid="send-subject"/></Field>
          <Field label="Corpo email"><RichTextEditor value={body} onChange={setBody} testId="send-body" minHeight={200}/></Field>
          <Button onClick={send} disabled={busy || !subject.trim() || !body.trim()} className="w-full bg-blue-800 hover:bg-blue-900" data-testid="send-confirm-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2"/> : <Send className="w-4 h-4 mr-2"/>}
            Invia email con PDF
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
